/**
 * usePremiumCTA — ORB CTA condicional
 *
 * Lee /api/auth/me + /api/pro/usage y expone `shouldShowPremiumCTA` = true
 * cuando:
 *   - el usuario NO es premium
 *   - ya hizo su primera acción útil (`first_action_at` presente)
 *   - consumió >=10 imágenes del mes (50% del límite free=15)
 *
 * El componente que decida montar el CTA solo necesita leer este flag.
 * No dispara toasts ni efectos: es read-only.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';

import { API_URL } from '../config/api';
import { getToken } from '../utils/authToken';

const CTA_THRESHOLD_IMAGES = 10; // 50% del free_limit=15

export default function usePremiumCTA() {
  const [shouldShowPremiumCTA, setShouldShowPremiumCTA] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const token = getToken();
        if (!token || token === 'guest') return;

        const headers = {};  // P2.1 cookie-only via axios.defaults

        const [meRes, usageRes] = await Promise.all([
          axios.get(`${API_URL}/auth/me`, { headers }),
          axios.get(`${API_URL}/pro/usage`, { headers }).catch(() => null),
        ]);

        if (cancelled) return;

        const isPremium = !!meRes.data?.is_premium;
        const hasFirstAction = !!meRes.data?.first_action_at;
        const freeUsed = usageRes?.data?.free_used ?? 0;

        const show = !isPremium && hasFirstAction && freeUsed >= CTA_THRESHOLD_IMAGES;

        setShouldShowPremiumCTA(show);
        setStats({
          isPremium,
          hasFirstAction,
          freeUsed,
          threshold: CTA_THRESHOLD_IMAGES,
        });
      } catch (_) {
        // Silencioso
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { shouldShowPremiumCTA, stats };
}
